import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Stack,
  CircularProgress,
  Divider,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import SchoolIcon from "@mui/icons-material/School";
import { useAuth } from "../context/AuthContext";

export default function TutorDashboard() {
  const { profile } = useAuth();
  const [tutorData, setTutorData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Default payroll range (last 14 days)
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  const fetchTutorData = useCallback(async () => {
    setLoading(true);

    // Fetch tutor profile, assignments, and logs
    const { data, error } = await supabase
      .from("tutors")
      .select(
        `id,
          profiles ( full_name, email ),
          student_tutor_assignments (
            id,
            rate,
            hours_bought,
            student_id,
            students ( id, full_name )
          ),
          lesson_logs ( hours, date, student_id, status, is_paid, rate )
        `,
      )
      .eq("id", profile.id)
      .single();

    if (error) {
      console.error("Error:", error);
    } else if (data) {
      const assignmentsWithStats = (data.student_tutor_assignments || []).map(
        (asg) => {
          const allLogsForThisStudent =
            data.lesson_logs?.filter(
              (log) => String(log.student_id) === String(asg.student_id),
            ) || [];

          // TOTAL HOURS USED   (For student budget calculation)
          // Must match the single definition of "used" shared by the
          // Students page, Logger's budget guard and get_tutor_overview:
          // approved *and* pending hours are committed against hours_bought,
          // only 'rejected' does not count. Counting approved-only here
          // overstated the balance a tutor could still log against.
          const totalUsedAllTime = allLogsForThisStudent
            .filter(
              (log) => log.status === "approved" || log.status === "pending",
            )
            .reduce((acc, log) => acc + (Number(log.hours) || 0), 0);

          // RECENT UNPAID HOURS (For earnings calculation)
          const recentUnpaidLogs = allLogsForThisStudent.filter(
            (log) =>
              log.status === "approved" &&
              log.is_paid === false &&
              log.date >= startDate &&
              log.date <= endDate,
          );

          const recentUnpaidHours = recentUnpaidLogs.reduce(
            (acc, log) => acc + (Number(log.hours) || 0),
            0,
          );

          // Price each session at the rate that was locked in when it was
          // logged (log.rate), not today's assignment rate — otherwise a
          // rate change reprices old, already-approved sessions. e.g. 5 hrs
          // logged at R150 plus 4 hrs logged at R160 should total
          // 5*150 + 4*160, not 9 * (current rate).
          const recentUnpaidEarnings = recentUnpaidLogs.reduce(
            (acc, log) =>
              acc + Number(log.hours) * Number(log.rate ?? asg.rate ?? 0),
            0,
          );

          const remaining = (Number(asg.hours_bought) || 0) - totalUsedAllTime;

          return {
            ...asg,
            hoursTaught: recentUnpaidHours,
            totalEarned: recentUnpaidEarnings,
            remainingHours: remaining,
          };
        },
      );

      const totalHours = assignmentsWithStats.reduce(
        (acc, asg) => acc + asg.hoursTaught,
        0,
      );
      const totalEarnings = assignmentsWithStats.reduce(
        (acc, asg) => acc + asg.totalEarned,
        0,
      );

      setTutorData({
        name: data.profiles?.full_name || "Tutor",
        email: data.profiles?.email,
        totalLoggedHours: totalHours,
        totalEarnings: totalEarnings,
        assignments: assignmentsWithStats,
      });
    }
    setLoading(false);
  }, [startDate, endDate, profile.id]);

  useEffect(() => {
    fetchTutorData();
  }, [fetchTutorData]);

  // -------------------- Render -------------------- //

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={20}>
        <CircularProgress size={60} />
      </Box>
    );

  if (!tutorData)
    return <Typography sx={{ p: 4 }}>No tutor profile found.</Typography>;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography
        variant="h4"
        sx={{ fontWeight: "bold", mb: 4, color: "primary.main" }}
      >
        My Earnings Dashboard
      </Typography>

      {/* Profile Header Card */}
      <Paper
        sx={{ p: 3, mb: 4, borderRadius: 2, border: "1px solid #e0e0e0" }}
        elevation={0}
      >
        <Stack direction="row" spacing={3} alignItems="center">
          <PersonIcon sx={{ fontSize: 40, color: "action.active" }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              {tutorData.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tutorData.email}
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography variant="caption" color="text.secondary">
              TOTAL EARNINGS
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="success.main">
              R
              {tutorData.totalEarnings.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Details Section */}
      <Paper
        sx={{ p: 3, borderRadius: 2, border: "1px solid #e0e0e0" }}
        elevation={0}
      >
        <Stack direction="row" alignItems="center" spacing={4} mb={3}>
          <SchoolIcon color="primary" />
          <Typography variant="h6">My Student Assignments</Typography>

          <Grid container spacing={2} alignItems="center">
            <Typography
              variant="body2"
              sx={{
                fontWeight: "500",
                color: "text.secondary",
                whiteSpace: "nowrap",
              }}
            >
              Date Range:
            </Typography>
            <TextField
              label="From"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: 100,
                bgcolor: "white",
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <TextField
              label="To"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: 100,
                bgcolor: "white",
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: "grey.50" }}>
              <TableRow>
                <TableCell>
                  <Typography variant="subtitle2">Student</Typography>
                </TableCell>

                <TableCell align="right">
                  <Typography variant="subtitle2">Approved Hours</Typography>
                </TableCell>

                <TableCell align="right">
                  <Typography variant="subtitle2">Hourly Rate</Typography>
                </TableCell>

                <TableCell align="right">
                  <Typography variant="subtitle2">Approved Earnings</Typography>
                </TableCell>

                <TableCell align="right">
                  <Typography variant="subtitle2">Remaining Hours</Typography>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {tutorData.assignments.map((asg) => (
                <TableRow key={asg.id} hover>
                  {" "}
                  <TableCell sx={{ fontWeight: 600 }}>
                    {asg.students?.full_name}
                  </TableCell>
                  <TableCell align="right">{asg.hoursTaught} hrs</TableCell>
                  <TableCell align="right">R{asg.rate}</TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: "bold", color: "success.main" }}
                  >
                    R{asg.totalEarned.toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{
                        color:
                          asg.remainingHours <= 0
                            ? "error.main"
                            : asg.remainingHours <= 2
                              ? "warning.main"
                              : "text.primary",
                      }}
                    >
                      {asg.remainingHours.toFixed(2)} hrs
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 2, display: "block" }}
      >
        * Showing approved, unpaid sessions from {startDate} to {endDate}.
      </Typography>
    </Container>
  );
}
